import { Autocomplete, Box, CircularProgress, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { adminApi } from "apis";
import {
  GetPracticesListSortByEnum,
  GetPracticesListSortOrderEnum,
  type PracticeListItemDto,
} from "libs/cellera-sdk";
import React, { useEffect, useState } from "react";

interface EFaxPracticeSelectorProps {
  value: string | null;
  onChange: (practiceId: string | null) => void;
}

const EFaxPracticeSelector: React.FC<EFaxPracticeSelectorProps> = ({
  value,
  onChange,
}) => {
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: ["efax-practice-options", debounced],
    queryFn: () =>
      adminApi.getAllPractices({
        search: debounced || undefined,
        page: 1,
        pageSize: 50,
        sortBy: GetPracticesListSortByEnum.Name,
        sortOrder: GetPracticesListSortOrderEnum.Asc,
      }),
    staleTime: 60_000,
  });

  const options = (data?.practices ?? []) as PracticeListItemDto[];
  const selected = options.find((p) => p.id === value) ?? null;

  return (
    <Box sx={{ minWidth: 280 }}>
      <Autocomplete<PracticeListItemDto>
        size="small"
        options={options}
        value={selected}
        onChange={(_, v) => onChange(v?.id ?? null)}
        inputValue={search}
        onInputChange={(_, v, reason) => {
          if (reason !== "reset") setSearch(v);
        }}
        getOptionLabel={(o) => o.name}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        loading={isLoading}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Practice"
            placeholder="Select a practice"
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? <CircularProgress size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
      />
    </Box>
  );
};

export default EFaxPracticeSelector;
